import React, { memo, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import RightModalLayout from "../layouts/Modal/RightModalLayout";
import { SIDEBAR_ACTIONS } from "../../constants/actionsConstants";
import WhatsappFlowModelMC from "../models/WhatsappFlowModelMC";
import LoadingMC from "../components/LoadingMC";

const HEALTH_COLORS = {
  AVAILABLE: 'tw-bg-green-100 tw-text-green-700',
  LIMITED: 'tw-bg-yellow-100 tw-text-yellow-700',
  BLOCKED: 'tw-bg-red-100 tw-text-red-700'
};

const WhatsappFlowDetailModal = memo(({ flow }) => {
  const { t } = useTranslation('WhatsappFlowsScreenMC');
  const dispatch = useDispatch();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const closeModal = () => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey: 'WhatsappFlowDetailModal' }
    });
  };

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const data = await WhatsappFlowModelMC.getDetail(flow.meta_flow_id);
        setDetail(data);
      } catch (e) {
        console.error('Error fetching flow detail', e);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [flow.meta_flow_id]);

  const healthStatus = detail?.raw_data?.health_status?.can_send_message;
  const healthColor = HEALTH_COLORS[healthStatus] || 'tw-bg-gray-100 tw-text-gray-700';
  const previewUrl = detail?.preview_url || flow.preview_url;
  const raw = detail?.raw_data || {};

  return (
    <RightModalLayout
      title={flow.name}
      closeAction={closeModal}
      footerVariant="NONE"
      className="!tw-p-0 !tw-space-y-0"
    >
      <div className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-overflow-y-auto">
        {loading ? (
          <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-py-20">
            <LoadingMC loading />
          </div>
        ) : (
          <>
            {/* Info section */}
            <div className="tw-flex tw-flex-col tw-gap-4 tw-p-6">
              <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                <div>
                  <p className="tw-text-xs tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-1">{t('status')}</p>
                  <span className="tw-text-sm tw-font-medium tw-text-gray-700">{detail?.status || flow.status}</span>
                </div>
                <div>
                  <p className="tw-text-xs tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-1">{t('categories_label')}</p>
                  <span className="tw-text-sm tw-text-gray-700">{(detail?.categories || flow.categories)?.join(', ') || '-'}</span>
                </div>
                {raw.json_version && (
                  <div>
                    <p className="tw-text-xs tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-1">JSON Version</p>
                    <span className="tw-text-sm tw-text-gray-700">{raw.json_version}</span>
                  </div>
                )}
                {healthStatus && (
                  <div>
                    <p className="tw-text-xs tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-1">Health</p>
                    <span className={`tw-inline-block tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${healthColor}`}>
                      {healthStatus}
                    </span>
                  </div>
                )}
              </div>

              {raw.endpoint_uri && (
                <div>
                  <p className="tw-text-xs tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-1">Endpoint</p>
                  <span className="tw-text-xs tw-break-all tw-text-gray-600">{raw.endpoint_uri}</span>
                </div>
              )}

              {raw.validation_errors?.length > 0 && (
                <div>
                  <p className="tw-text-xs tw-text-red-500 tw-uppercase tw-tracking-wide tw-mb-1">{t('errors_label', { defaultValue: 'Errores' })}</p>
                  <ul className="tw-list-disc tw-pl-4 tw-text-xs tw-text-red-600 tw-space-y-1">
                    {raw.validation_errors.map((err, i) => (
                      <li key={i}>{typeof err === 'string' ? err : err.error_description || JSON.stringify(err)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Preview section */}
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-gray-50 tw-py-10 tw-px-6 tw-border-t tw-border-gray-100">
              {previewUrl ? (
                <div className="tw-relative tw-w-full tw-flex tw-flex-col tw-items-center">
                  <p className="tw-mb-8 tw-text-lg tw-font-semibold tw-text-gray-700">{t('flow_preview_label', { defaultValue: 'Vista previa del Flow' })}</p>

                  <div className="tw-relative tw-mx-auto tw-bg-white tw-rounded-2xl tw-shadow-xl tw-border tw-border-gray-200 tw-overflow-hidden tw-w-full tw-max-w-[800px] tw-h-[800px]">
                    <iframe
                      src={previewUrl}
                      title={`Preview: ${flow.name}`}
                      className="tw-w-full tw-h-full"
                      style={{ border: 'none' }}
                    />
                  </div>

                  <p className="tw-mt-8 tw-text-sm tw-text-gray-400 tw-italic">
                    * La apariencia final puede variar levemente en el dispositivo del usuario.
                  </p>
                </div>
              ) : (
                <div className="tw-py-20 tw-px-10 tw-bg-white tw-rounded-2xl tw-border-2 tw-border-dashed tw-border-gray-200 tw-text-center">
                  <p className="tw-text-gray-400 tw-text-base tw-max-w-xs">
                    {t('preview_unavailable_detail', { defaultValue: 'No hay vista previa disponible para este flow. Sincroniza los flows para generar la URL de preview.' })}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </RightModalLayout>
  );
});

WhatsappFlowDetailModal.displayName = "WhatsappFlowDetailModal";

export default WhatsappFlowDetailModal;
